// deps
import React, { memo } from "react";

// internal-deps
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { AddButton } from "@swiggy-private/dls-web-core/dist/components/AddButton";
import { TextField } from "@swiggy-private/dls-web-core/dist/components/TextField";
import { TextFieldTypes } from "@swiggy-private/dls-web-core/dist/components/TextField/types";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// styles
import { ItemWrapper, Avatar, InfoSection, InputWrapper } from "./styled";

// constants
import {
    RUPEE_SYMBOL,
    MIN_SHARE_VALUE,
    DEFAULT_AVATAR_IMAGE_ID,
    AVATAR_SIZE,
    TEXT_FIELD_PADDING,
    BASE64_IMG_PREFIX,
} from "../../../../constants";
import { SPLIT_BILL_TEST_IDS } from "@swiggy-private/payment-test-ids";

// interface
import { SplitMethod } from "../../../../interfaces";

// Base shared props
interface IContactListItemBaseProps {
    name: string;
    showSubtext?: boolean;
    subText?: string;
    avatar?: string;
}

// Variant for AMOUNT and PERCENTAGE - requires controlled input
interface IContactListItemInputVariant extends IContactListItemBaseProps {
    splitType: SplitMethod.AMOUNT | SplitMethod.PERCENTAGE;
    inputSymbol?: string;
    inputValue?: number;
    onInputChange: (value: string) => void;
    onInputBlur?: (value: string) => void;
    inputMin?: number;
    inputMax?: number;
    inputStep?: number;
}

// Variant for SHARE - requires button controls
interface IContactListItemButtonVariant extends IContactListItemBaseProps {
    splitType: SplitMethod.SHARE;
    buttonValue?: number;
    onIncrement: () => void;
    onDecrement: () => void;
}

// Discriminated union
export type IContactListItemProps = IContactListItemInputVariant | IContactListItemButtonVariant;

// Type guard functions
const isInputVariant = (props: IContactListItemProps): props is IContactListItemInputVariant => {
    return props.splitType === SplitMethod.AMOUNT || props.splitType === SplitMethod.PERCENTAGE;
};

const isButtonVariant = (props: IContactListItemProps): props is IContactListItemButtonVariant => {
    return props.splitType === SplitMethod.SHARE;
};

const ContactListItem: React.FC<IContactListItemProps> = (props) => {
    const { name, showSubtext = true, subText, avatar } = props;

    // Construct avatar URL: use base64 if available, otherwise use default cloudinary image
    const avatarUrl = avatar ? BASE64_IMG_PREFIX + avatar : undefined;

    return (
        <ItemWrapper>
            <Avatar>
                {avatarUrl ? (
                    <Image src={avatarUrl} width={AVATAR_SIZE} height={AVATAR_SIZE} />
                ) : (
                    <Image cloudinaryId={DEFAULT_AVATAR_IMAGE_ID} width={AVATAR_SIZE} height={AVATAR_SIZE} />
                )}
            </Avatar>
            <InfoSection>
                <Text variant="V2.body1.neutral.medium" color="text_High_Emphasis">
                    {name}
                </Text>
                {showSubtext && (
                    <Text variant="V2.body2.neutral.regular" color="text_Med_Emphasis">
                        {subText}
                    </Text>
                )}
            </InfoSection>

            {isInputVariant(props) && (
                <InputWrapper>
                    <Text variant="V2.body1.neutral.medium" color="text_High_Emphasis">
                        {props.inputSymbol ?? RUPEE_SYMBOL}
                    </Text>
                    <TextField
                        textFieldDetails={{
                            value: props.inputValue?.toString() || "",
                            onChange: (value: string) => props.onInputChange(value),
                            onBlur: (value: string) => props.onInputBlur?.(value),
                            type: TextFieldTypes.NUMBER,
                            name: "splitAmount",
                            label: "", // not needed
                            attrs: {
                                min: props.inputMin ?? MIN_SHARE_VALUE,
                                max: props.inputMax,
                                step: props.inputStep,
                                "data-testid": SPLIT_BILL_TEST_IDS.AMOUNT_INPUT,
                            },
                            inputStyle: {
                                padding: TEXT_FIELD_PADDING,
                                height: "auto",
                                borderRadius: 4,
                            },
                        }}
                    />
                </InputWrapper>
            )}
            {isButtonVariant(props) && (
                <div data-testid={SPLIT_BILL_TEST_IDS.SHARE_CONTROL}>
                    <AddButton
                        value={props.buttonValue ?? MIN_SHARE_VALUE}
                        onClickIncrement={props.onIncrement}
                        onClickDecrement={props.onDecrement}
                        size="small"
                    />
                </div>
            )}
        </ItemWrapper>
    );
};

export default memo(ContactListItem);
